import { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import './ProjectBudget.css';
import IconButton from '@mui/material/IconButton';
import axiosInstance from '../../../Config/axiosInstance';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import EditNoteSharpIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';

function ProjectBudget() {
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  const [canDelete, setCanDelete] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('');

  const formatNumber = (value, locale = 'ro-RO') => {
    if (value === null || value === undefined || value === '') return '';

    const n = Number(value);
    if (Number.isNaN(n)) return '';

    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  };

  useEffect(() => {
    const requiredPath = "/project-budget";
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    const hasCreatePermission = permissions.some((page) => {
      const pagePath = new URL(page.url).pathname;
      return pagePath === requiredPath && page.can_create === true;
    });
    setCanCreate(hasCreatePermission);

    const hasEditPermission = permissions.some((page) => {
      const pagePath = new URL(page.url).pathname;
      return pagePath === requiredPath && page.can_edit === true;
    });
    setCanEdit(hasEditPermission);

    setCanDelete(
      permissions.some((page) =>
        new URL(page.url).pathname === requiredPath && page.can_delete
      )
    );
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          budgetResponse,
          budgetStructure,
          suppliersResponse,
          internalResourceResponse,
          currenciesResponse,
          systemCurrencyResponse
        ] = await Promise.all([
          axiosInstance.get('/project-budget/'),
          axiosInstance.get('/budget-structure/'),
          axiosInstance.get('/suppliers/'),
          axiosInstance.get('/internal-resources/'),
          axiosInstance.get('/currencies/'),
          axiosInstance.get('/system-currency/'),
        ]);

        const BSOfferCodeMap = budgetStructure.data.reduce((acc, item) => {
          acc[item.id] = item.offer_code_details?.offer_code || '';
          return acc;
        }, {});

        const ProjectCodeMap = budgetStructure.data.reduce((acc, item) => {
          acc[item.id] = item.project_code_details?.project_code || '';
          return acc;
        }, {});

        const CostIdMap = budgetStructure.data.reduce((acc, item) => {
          acc[item.id] = item.cost_element_id || '';
          return acc;
        }, {});

        const CostDescriptionMap = budgetStructure.data.reduce((acc, item) => {
          acc[item.id] = item.cost_element_description || '';
          return acc;
        }, {});

        const supplierMap = suppliersResponse.data.reduce((acc, supplier) => {
          acc[supplier.id] = supplier.name || '';
          return acc;
        }, {});

        const internalResourceMap = internalResourceResponse.data.reduce((acc, item) => {
          acc[item.id] = item.resource_name || '';
          return acc;
        }, {});

        const currenciesMap = currenciesResponse.data.reduce((acc, item) => {
          acc[item.id] = item.abbreviation || '';
          return acc;
        }, {});

        const systemCurrencyId =
          Array.isArray(systemCurrencyResponse.data) && systemCurrencyResponse.data.length > 0
            ? systemCurrencyResponse.data[0]?.currency
            : null;

        const systemCurrencyAbbreviation = systemCurrencyId
          ? currenciesMap[systemCurrencyId] || ''
          : '';

        const sortedData = budgetResponse.data.map((item) => {
          const currencyAbbreviation = currenciesMap[item.currency] || '';
          const systemCurrency =
            item.cost_offer_without_vat_system_currency ||
            item.cost_po_without_vat_system_currency ||
            systemCurrencyAbbreviation;

          return {
            id: item.id,
            codOferta: BSOfferCodeMap[item.cost_element] || '-',
            projectCodes: ProjectCodeMap[item.cost_element] || '-',
            costId: CostIdMap[item.cost_element] || '-',
            costDescription: CostDescriptionMap[item.cost_element] || '-',
            supplier: internalResourceMap[item.internal_resource] || supplierMap[item.supplier] || '-',
            source: item.source_type || '',

            cost_offer_without_VAT: item.cost_offer_without_vat
              ? `${formatNumber(item.cost_offer_without_vat)} ${currencyAbbreviation}`
              : `0,00 ${currencyAbbreviation}`,

            cost_PO_without_VAT: item.cost_po_without_vat
              ? `${formatNumber(item.cost_po_without_vat)} ${currencyAbbreviation}`
              : `0,00 ${currencyAbbreviation}`,

            cost_offer_without_VAT_system: item.cost_offer_without_vat_system
              ? `${formatNumber(item.cost_offer_without_vat_system)} ${systemCurrency}`
              : `0,00 ${systemCurrency}`,

            cost_PO_without_VAT_system: item.cost_po_without_vat_system
              ? `${formatNumber(item.cost_po_without_vat_system)} ${systemCurrency}`
              : `0,00 ${systemCurrency}`,
          };
        }).sort((a, b) => b.id - a.id);

        setRows(sortedData);
      } catch (error) {
        console.error('Eroare la obținerea datelor:', error.response?.data || error.message);
      }
    };

    fetchData();
  }, []);

  const handleAddClick = () => {
    navigate('/project-budget/project-budget/add-project-budget');
  };

  const handleEditClick = (row) => {
    navigate(`/project-budget/project-budget/edit-project-budget`, {
      state: { id: row.id }
    });
  };

  const handleDeleteClick = (row) => {
    setItemToDelete(row);
    setOpenDeleteDialog(true);
  };

  const columns = [
    { field: 'codOferta', headerName: 'Offer Code', flex: 1, minWidth: 120 },
    { field: 'projectCodes', headerName: 'Project Code', flex: 1, minWidth: 200 },
    { field: 'costId', headerName: 'Cost ID', flex: 1, minWidth: 105 },
    { field: 'costDescription', headerName: 'Cost Description', flex: 2, minWidth: 250 },
    { field: 'supplier', headerName: 'Supplier', flex: 1, minWidth: 180 },
    { field: 'source', headerName: 'Source', flex: 1, minWidth: 90 },
    {
      field: 'cost_offer_without_VAT',
      headerName: 'Cost offer without VAT',
      flex: 1,
      minWidth: 160,
      renderHeader: () => (
        <div style={{
          whiteSpace: 'normal',
          lineHeight: '16px',
          textAlign: 'center'
        }}>
          Cost offer without VAT
        </div>
      ),
    },

    {
      field: 'cost_offer_without_VAT_system',
      headerName: 'Cost offer without VAT System',
      flex: 1,
      minWidth: 210,
      renderHeader: () => (
        <div style={{
          whiteSpace: 'normal',
          lineHeight: '16px',
          textAlign: 'center'
        }}>
          Cost offer without VAT System
        </div>
      ),
    },

    {
      field: 'cost_PO_without_VAT',
      headerName: 'Cost PO without VAT',
      flex: 1,
      minWidth: 160,
      renderHeader: () => (
        <div style={{
          whiteSpace: 'normal',
          lineHeight: '16px',
          textAlign: 'center'
        }}>
          Cost PO without VAT
        </div>
      ),
    },

    {
      field: 'cost_PO_without_VAT_system',
      headerName: 'Cost PO without VAT System',
      flex: 1,
      minWidth: 210,
      renderHeader: () => (
        <div style={{
          whiteSpace: 'normal',
          lineHeight: '16px',
          textAlign: 'center'
        }}>
          Cost PO without VAT System
        </div>
      ),
    },
  ];

  if (canEdit || canDelete) {
    columns.push({
      field: 'action',
      headerName: 'Action',
      width: 100,
      renderCell: (params) => (
        <>
          {canEdit && (
            <IconButton onClick={() => handleEditClick(params.row)} sx={{ color: '#1976d2' }}>
              <EditNoteSharpIcon />
            </IconButton>
          )}
          {canDelete && (
            <IconButton onClick={() => handleDeleteClick(params.row)} sx={{ color: '#d32f2f' }}>
              <DeleteIcon />
            </IconButton>
          )}
        </>
      ),
      sortable: false,
      disableColumnMenu: true,
    });
  }

  return (
    <div className="project-budget">
      {canCreate && (
        <div className="add-button-container">
          <Button
            id="add-project-budget"
            variant="contained"
            onClick={handleAddClick}
            disabled={!canCreate}
            sx={{ textTransform: "none" }}
            style={{
              float: 'right',
              marginBottom: '15px',
            }}
          >
            <AddIcon />
            Add Project Budget
          </Button>
        </div>
      )}

      <div className="table-container">
        <DataGrid
          id="project-budget-table"
          rows={rows}
          columns={columns}
          columnHeaderHeight={70}
          pageSize={5}
          initialState={{
            sorting: {
              sortModel: [{ field: 'id', sort: 'desc' }]
            },
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          disableRowSelectionOnClick={true}
          sx={{
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .Mui-selected:hover": {
              backgroundColor: "transparent !important"
            },
          }}
          pageSizeOptions={[10, 20, 25]}
          rowHeight={40}
        />
      </div>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this record?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            sx={{
              backgroundColor: '#e0e0e0',
              color: '#424242',
              '&:hover': {
                backgroundColor: '#bdbdbd',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              try {
                await axiosInstance.delete(`/project-budget/${itemToDelete.id}/`);
                setRows(prevRows => prevRows.filter(r => r.id !== itemToDelete.id));
                setOpenDeleteDialog(false);
              } catch (error) {
                const message =
                  error.response?.data?.error ||
                  error.response?.data?.detail ||
                  "An unexpected error occurred while deleting.";
                setDeleteErrorMessage(message);
                setOpenDeleteDialog(false);
                setErrorDialogOpen(true);
              }
            }}
            sx={{
              backgroundColor: '#d32f2f',
              color: 'white',
              '&:hover': {
                backgroundColor: '#b71c1c',
              }
            }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
      >
        <DialogTitle id="error-dialog-title" style={{ color: 'red' }}>
          Delete Failed
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="error-dialog-description">
            {deleteErrorMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setErrorDialogOpen(false)}
            sx={{
              backgroundColor: '#e0e0e0',
              color: '#424242',
              '&:hover': {
                backgroundColor: '#bdbdbd',
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ProjectBudget;